import { useEffect, useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';
import { Sliders, Loader2, Save, RotateCcw, Search, Edit3 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAIPrompts() {
  const { authFetch } = useAdmin();
  const [prompts, setPrompts] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // prompt object being edited
  const [draft, setDraft] = useState({ system_message: '', provider: '', model: '' });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch(`${API}/admin/llm/prompts`);
    const d = await r.json();
    setPrompts(d.items || []);
    setProviders(d.providers || []);
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  function openEdit(p) {
    setEditing(p);
    setDraft({
      system_message: p.effective_system || '',
      provider: p.effective_provider || 'gemini',
      model: p.effective_model || 'gemini-2.5-flash',
    });
  }

  async function save() {
    if (!editing) return;
    setSaving(true);
    const r = await authFetch(`${API}/admin/llm/prompts/${encodeURIComponent(editing.key)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        system_message: draft.system_message?.trim() || null,
        provider: draft.provider || null,
        model: draft.model || null,
      }),
    });
    setSaving(false);
    if (r.ok) {
      toast.success('Prompt spremljen.');
      setEditing(null);
      load();
    } else {
      const e = await r.json().catch(() => ({}));
      toast.error(e.detail || 'Greška pri spremanju.');
    }
  }

  async function reset(key) {
    if (!confirm(`Vratiti prompt "${key}" na zadanu vrijednost iz koda?`)) return;
    const r = await authFetch(`${API}/admin/llm/prompts/${encodeURIComponent(key)}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Prompt vraćen na default.'); load(); }
    else toast.error('Greška.');
  }

  const filtered = prompts.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.key.toLowerCase().includes(q)
        || (p.location || '').toLowerCase().includes(q)
        || (p.description || '').toLowerCase().includes(q)
        || (p.effective_model || '').toLowerCase().includes(q);
  });

  return (
    <div className="p-6 max-w-[1400px]" data-testid="admin-ai-prompts-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl text-zinc-100 flex items-center gap-3">
            <Sliders size={28} className="text-red-500" /> AI promptovi (centralna kontrola)
          </h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Svaki AI prompt na platformi registriran je s ključem i lokacijom u kodu. Ovdje možeš mijenjati sistemski tekst i odabrati LLM model — promjene se čuvaju u <code>ai_prompts</code> kolekciji i odmah primjenjuju bez deploya. Default model je Gemini 2.5 Flash (besplatno preko <code>GEMINI_API_KEY</code>).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Pretraži (ključ, ruta, model)…"
                   data-testid="prompt-search"
                   className="bg-zinc-900 border border-zinc-800 rounded-md pl-7 pr-3 py-1.5 text-xs w-72" />
          </div>
        </div>
      </header>

      {loading ? (
        <div className="p-10 text-center text-zinc-500"><Loader2 className="inline animate-spin mr-2" /> Učitavanje…</div>
      ) : (
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-900/60 text-zinc-400">
              <tr>
                <th className="text-left px-3 py-2.5">Prompt ključ / lokacija</th>
                <th className="text-left px-3 py-2.5">Opis</th>
                <th className="text-left px-3 py-2.5">Aktivni model</th>
                <th className="text-center px-3 py-2.5">Stanje</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.key} className="border-t border-zinc-800/50 hover:bg-zinc-900/40 align-top" data-testid={`prompt-row-${p.key}`}>
                  <td className="px-3 py-3">
                    <div className="font-mono text-zinc-200">{p.key}</div>
                    <div className="text-[10px] text-zinc-500 mt-1">{p.location}</div>
                  </td>
                  <td className="px-3 py-3 text-zinc-400 max-w-md">{p.description}</td>
                  <td className="px-3 py-3">
                    <div className="text-zinc-300">{p.effective_model}</div>
                    <div className="text-[10px] text-zinc-500">{p.effective_provider}</div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {p.is_overridden
                      ? <span className="px-2 py-0.5 rounded bg-amber-900/30 text-amber-400 text-[10px]">override</span>
                      : <span className="px-2 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[10px]">default</span>}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(p)} data-testid={`edit-${p.key}`}
                            className="px-2 py-1 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-200 inline-flex items-center gap-1 mr-1">
                      <Edit3 size={10} /> Uredi
                    </button>
                    {p.is_overridden && (
                      <button onClick={() => reset(p.key)} data-testid={`reset-${p.key}`}
                              className="px-2 py-1 rounded bg-zinc-900 border border-zinc-800 hover:border-amber-700 text-zinc-300 inline-flex items-center gap-1">
                        <RotateCcw size={10} /> Reset
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={5} className="text-center text-zinc-600 py-10">Nema rezultata.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
               onClick={(e) => e.stopPropagation()} data-testid="prompt-edit-modal">
            <header className="p-4 border-b border-zinc-800">
              <div className="font-mono text-sm text-zinc-200">{editing.key}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">{editing.location}</div>
              <div className="text-xs text-zinc-400 mt-2">{editing.description}</div>
            </header>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">LLM provider / model</label>
                <select value={`${draft.provider}/${draft.model}`}
                        onChange={(e) => { const [p, m] = e.target.value.split('/'); setDraft(d => ({ ...d, provider: p, model: m })); }}
                        data-testid="prompt-model-select"
                        className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm">
                  {providers.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
                <p className="text-[10px] text-zinc-500 mt-1">
                  Savjet: koristi <strong>Gemini 2.5 Flash</strong> ili <strong>Lite</strong> za 95% slučajeva — ide preko besplatnog ključa.
                </p>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Sistemski prompt</label>
                <textarea rows={14} value={draft.system_message}
                          onChange={(e) => setDraft(d => ({ ...d, system_message: e.target.value }))}
                          data-testid="prompt-system-textarea"
                          className="w-full bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-xs font-mono text-zinc-100 focus:border-red-500 outline-none" />
                <p className="text-[10px] text-zinc-500 mt-1">
                  Ostavi prazno za default iz koda. Ako prompt poziva svoj inline `system_message` runtime-a, ovaj override ima prednost.
                </p>
              </div>
            </div>
            <footer className="p-4 border-t border-zinc-800 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} data-testid="prompt-cancel"
                      className="px-3 py-1.5 text-xs rounded-md bg-zinc-900 border border-zinc-800 hover:border-zinc-700">Odustani</button>
              <button onClick={save} disabled={saving} data-testid="prompt-save"
                      className="px-4 py-1.5 text-xs rounded-md bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-1.5 disabled:opacity-50">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Spremi override
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
